<template>
  <div>
    <!-- Side tab that's always visible - redesigned for better display -->
    <div 
      class="feedback-tab-container" 
      @click="toggleFeedback"
      :class="{ 'tab-open': isOpen }"
      aria-label="Open feedback form"
      role="button"
      tabindex="0"
      @keydown.enter="toggleFeedback"
      @keydown.space.prevent="toggleFeedback"
    >
      <div class="tab-content">
        <div class="tab-icon">
          <Icon icon="mdi:message-text" width="20" height="20" />
        </div>
        <span class="tab-text">Feedback</span>
      </div>
    </div>

    <!-- Modal that appears when the tab is clicked -->
    <transition name="modal">
      <div 
        v-if="isOpen" 
        class="modal-overlay"
        aria-modal="true"
        role="dialog"
        aria-labelledby="feedback-title"
        @click="toggleFeedback"
      >
        <div 
          class="feedback-modal"
          @click.stop
        >
          <!-- Close button -->
          <button 
            @click="toggleFeedback" 
            class="modal-close"
            aria-label="Close feedback form"
          >
            <Icon icon="mdi:close" width="20" height="20" />
          </button>
          
          <!-- Header -->
          <div class="modal-header">
            <div class="header-icon">
              <Icon icon="mdi:message-text" width="32" height="32" />
            </div>
            <div class="header-content">
              <h2 id="feedback-title">We Value Your Feedback</h2>
              <p class="header-subtitle">Help us improve your experience</p>
            </div>
          </div>
          
          <!-- Success state -->
          <div v-if="submitted" class="success-container">
            <div class="success-icon">
              <Icon icon="mdi:check-circle" width="48" height="48" />
            </div>
            <h3>Thank you for your feedback!</h3>
            <p class="success-message">We appreciate your input and will use it to improve our services.</p>
            <p v-if="form.requestFollowup" class="followup-message">
              We'll follow up with you at <strong>{{ form.email }}</strong> within 2 business days.
            </p>
            <button 
              @click="resetForm" 
              class="btn btn-primary"
            >
              <Icon icon="mdi:plus" width="20" height="20" />
              Send Another Feedback
            </button>
          </div>
          
          <!-- Single step form - simplified like quote process -->
          <form v-else @submit.prevent="submitFeedback" class="form-container">
            <!-- Feedback type and message in a compact layout -->
            <div class="form-row">
              <div class="form-group form-group-half">
                <label class="form-label" for="feedbackType">
                  Type
                </label>
                <div class="select-wrapper">
                  <select 
                    id="feedbackType" 
                    v-model="form.type" 
                    class="form-select"
                    required
                  >
                    <option value="">Select type</option>
                    <option value="general">General</option>
                    <option value="suggestion">Suggestion</option>
                    <option value="bug">Bug Report</option>
                    <option value="compliment">Compliment</option>
                  </select>
                  <Icon icon="mdi:chevron-down" class="select-icon" width="20" height="20" />
                </div>
              </div>
              
              <div class="form-group form-group-half">
                <label class="form-label" for="name">
                  Name (Optional)
                </label>
                <input 
                  id="name" 
                  v-model="form.name" 
                  class="form-input"
                  type="text"
                  placeholder="Your name"
                >
              </div>
            </div>
            
            <div class="form-group">
              <label class="form-label" for="feedback">
                Your Feedback<span class="required">*</span>
              </label>
              <textarea 
                id="feedback" 
                v-model="form.message" 
                class="form-textarea"
                rows="3"
                required
                aria-required="true"
                placeholder="Tell us about your experience..."
              ></textarea>
            </div>
            
            <!-- Contact info in a compact row -->
            <div class="form-row">
              <div class="form-group form-group-half">
                <label class="form-label" for="email">
                  Email (Optional)
                </label>
                <input 
                  id="email" 
                  v-model="form.email" 
                  class="form-input"
                  type="email"
                  placeholder="your@email.com"
                >
              </div>
              
              <div class="form-group form-group-half">
                <label class="form-label" for="attachment">
                  Screenshot (Optional)
                </label>
                <div class="file-upload-compact" :class="{ 'has-file': form.attachment }">
                  <input 
                    ref="fileInput"
                    type="file" 
                    id="attachment" 
                    @change="handleFileUpload" 
                    accept="image/*"
                    class="file-input"
                    aria-label="Upload screenshot"
                  >
                  <div class="file-upload-content">
                    <Icon 
                      :icon="form.attachment ? 'mdi:check-circle' : 'mdi:cloud-upload'" 
                      width="20" 
                      height="20" 
                      class="file-icon"
                    />
                    <span class="file-text">
                      {{ form.attachment ? form.attachmentName : 'Upload file' }}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            <!-- Follow-up option -->
            <div class="checkbox-group">
              <label class="checkbox-label">
                <input 
                  id="requestFollowup" 
                  v-model="form.requestFollowup" 
                  type="checkbox" 
                  class="checkbox-input"
                  :disabled="!form.email"
                >
                <span class="checkbox-custom"></span>
                <span class="checkbox-text">
                  I'd like to receive a follow-up response
                  <span v-if="!form.email" class="text-muted">(requires email)</span>
                </span>
              </label>
            </div>
            
            <button 
              type="submit" 
              class="btn btn-primary btn-full"
              :disabled="!form.type || !form.message || isSubmitting"
            >
              <span v-if="isSubmitting">
                <Icon icon="mdi:loading" class="animate-spin" width="20" height="20" />
                Sending...
              </span>
              <span v-else>
                <Icon icon="mdi:send" width="20" height="20" />
                Send Feedback
              </span>
            </button>
          </form>
        </div>
      </div>
    </transition>
  </div>
</template>

<style scoped>
/* Modern Feedback Tab - 2025 Design System - Light Mode Only */

/* Tab Container */
.feedback-tab-container {
  position: fixed;
  right: 0;
  top: 50%;
  transform: translateY(-50%);
  z-index: 50;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.tab-content {
  background: linear-gradient(135deg, var(--color-primary, #DB0908) 0%, #c53030 100%);
  color: white;
  border-radius: 12px 0 0 12px;
  box-shadow: 
    0 4px 12px rgba(219, 9, 8, 0.3),
    0 2px 4px rgba(0, 0, 0, 0.1);
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 16px 12px;
  min-height: 120px;
  width: 48px;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  backdrop-filter: blur(10px);
}

.tab-content:hover {
  background: linear-gradient(135deg, #c53030 0%, #a53030 100%);
  transform: translateX(-4px);
  box-shadow: 
    0 6px 16px rgba(219, 9, 8, 0.4),
    0 4px 8px rgba(0, 0, 0, 0.15);
}

.tab-icon {
  margin-bottom: 8px;
  transition: transform 0.2s ease;
}

.tab-content:hover .tab-icon {
  transform: scale(1.1);
}

.tab-text {
  writing-mode: vertical-rl;
  text-orientation: mixed;
  font-weight: 600;
  font-size: 13px;
  letter-spacing: 0.5px;
  line-height: 1.2;
}

/* Modal Overlay */
.modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.6);
  backdrop-filter: blur(8px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 60;
  padding: 20px;
}

/* Modal Container */
.feedback-modal {
  background: white;
  color: #1f2937;
  border-radius: 20px;
  box-shadow: 
    0 20px 40px rgba(0, 0, 0, 0.15),
    0 8px 16px rgba(0, 0, 0, 0.1);
  width: 100%;
  max-width: 520px;
  max-height: 90vh;
  overflow-y: auto;
  position: relative;
  padding: 32px;
  margin: auto;
}

/* Close Button */
.modal-close {
  position: absolute;
  top: 20px;
  right: 20px;
  background: #f3f4f6;
  border: none;
  border-radius: 50%;
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #6b7280;
  cursor: pointer;
  transition: all 0.2s ease;
}

.modal-close:hover {
  background: #e5e7eb;
  color: #374151;
  transform: scale(1.05);
}

/* Header */
.modal-header {
  display: flex;
  align-items: center;
  gap: 16px;
  margin-bottom: 32px;
}

.header-icon {
  background: rgba(219, 9, 8, 0.1);
  border-radius: 16px;
  padding: 12px;
  color: var(--color-primary, #DB0908);
}

.header-content h2 {
  font-size: 24px;
  font-weight: 700;
  color: #1f2937;
  margin: 0 0 4px 0;
  line-height: 1.2;
}

.header-subtitle {
  font-size: 14px;
  color: #6b7280;
  margin: 0;
}

/* Success State */
.success-container {
  text-align: center;
  padding: 20px 0;
  display: flex;
  flex-direction: column;
  align-items: center;
}

.success-icon {
  background: linear-gradient(135deg, #10b981, #059669);
  border-radius: 50%;
  width: 80px;
  height: 80px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 20px;
  color: white;
}

.success-container h3 {
  font-size: 20px;
  font-weight: 600;
  color: #1f2937;
  margin: 0 0 12px 0;
}

.success-message {
  font-size: 16px;
  color: #6b7280;
  margin: 0 0 16px 0;
  line-height: 1.5;
}

.followup-message {
  font-size: 14px;
  color: #374151;
  margin: 0 0 24px 0;
  padding: 12px;
  background: #f3f4f6;
  border-radius: 8px;
}

.success-container .btn {
  margin: 0 auto;
  width: auto;
  min-width: 200px;
}

/* Form Styles */
.form-container {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.form-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
}

.form-group {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.form-group-half {
  /* Styles for half-width form groups in rows */
}

.form-label {
  font-size: 14px;
  font-weight: 600;
  color: #374151;
}

.required {
  color: var(--color-primary, #DB0908);
  margin-left: 2px;
}

/* Input Styles */
.form-input,
.form-textarea {
  padding: 12px 16px;
  border: 2px solid #e5e7eb;
  border-radius: 12px;
  font-size: 16px;
  color: #1f2937;
  background: white;
  transition: all 0.2s ease;
}

.form-input:focus,
.form-textarea:focus {
  outline: none;
  border-color: var(--color-primary, #DB0908);
  box-shadow: 0 0 0 3px rgba(219, 9, 8, 0.1);
}

.form-textarea {
  resize: vertical;
  min-height: 100px;
}

/* Select Styles */
.select-wrapper {
  position: relative;
}

.form-select {
  appearance: none;
  padding: 12px 48px 12px 16px;
  border: 2px solid #e5e7eb;
  border-radius: 12px;
  font-size: 16px;
  color: #1f2937;
  background: white;
  width: 100%;
  cursor: pointer;
  transition: all 0.2s ease;
}

.form-select:focus {
  outline: none;
  border-color: var(--color-primary, #DB0908);
  box-shadow: 0 0 0 3px rgba(219, 9, 8, 0.1);
}

.select-icon {
  position: absolute;
  right: 16px;
  top: 50%;
  transform: translateY(-50%);
  color: #6b7280;
  pointer-events: none;
}

/* Checkbox Styles */
.checkbox-group {
  margin: 16px 0;
}

.checkbox-label {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  cursor: pointer;
  font-size: 14px;
  line-height: 1.5;
}

.checkbox-input {
  position: absolute;
  opacity: 0;
  pointer-events: none;
}

.checkbox-custom {
  width: 20px;
  height: 20px;
  border: 2px solid #d1d5db;
  border-radius: 4px;
  background: white;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  flex-shrink: 0;
  margin-top: 2px;
}

.checkbox-input:checked + .checkbox-custom {
  background: var(--color-primary, #DB0908);
  border-color: var(--color-primary, #DB0908);
}

.checkbox-input:checked + .checkbox-custom::after {
  content: '✓';
  color: white;
  font-size: 12px;
  font-weight: bold;
}

.checkbox-input:disabled + .checkbox-custom {
  background: #f3f4f6;
  border-color: #d1d5db;
  cursor: not-allowed;
}

.checkbox-text {
  color: #374151;
}

.text-muted {
  color: #9ca3af;
  font-size: 13px;
}

/* File Upload */
.file-upload {
  border: 2px dashed #d1d5db;
  border-radius: 12px;
  padding: 24px;
  text-align: center;
  cursor: pointer;
  transition: all 0.2s ease;
  background: #fafafa;
  position: relative;
}

.file-upload:hover {
  border-color: var(--color-primary, #DB0908);
  background: rgba(219, 9, 8, 0.02);
}

.file-upload.has-file {
  border-color: #10b981;
  background: rgba(16, 185, 129, 0.05);
}

/* Compact File Upload for form rows */
.file-upload-compact {
  border: 2px dashed #d1d5db;
  border-radius: 8px;
  padding: 12px;
  text-align: center;
  cursor: pointer;
  transition: all 0.2s ease;
  background: #fafafa;
  position: relative;
  min-height: 44px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.file-upload-compact:hover {
  border-color: var(--color-primary, #DB0908);
  background: rgba(219, 9, 8, 0.02);
}

.file-upload-compact.has-file {
  border-color: #10b981;
  background: rgba(16, 185, 129, 0.05);
}

.file-input {
  position: absolute;
  inset: 0;
  opacity: 0;
  cursor: pointer;
}

.file-upload-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
}

.file-upload-compact .file-upload-content {
  flex-direction: row;
  gap: 8px;
}

.file-icon {
  color: #6b7280;
  transition: color 0.2s ease;
}

.file-upload:hover .file-icon,
.file-upload-compact:hover .file-icon {
  color: var(--color-primary, #DB0908);
}

.file-upload.has-file .file-icon,
.file-upload-compact.has-file .file-icon {
  color: #10b981;
}

.file-text {
  font-size: 14px;
  font-weight: 500;
  color: #374151;
}

.file-upload-compact .file-text {
  font-size: 13px;
}

.file-hint {
  font-size: 12px;
  color: #9ca3af;
}

/* Button Styles */
.btn {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 12px 24px;
  border: none;
  border-radius: 12px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  text-decoration: none;
  white-space: nowrap;
  min-height: 48px;
}

.btn span {
  display: flex;
  align-items: center;
  gap: 8px;
}

.btn-primary {
  background: linear-gradient(135deg, var(--color-primary, #DB0908), #c53030);
  color: white;
  box-shadow: 0 2px 4px rgba(219, 9, 8, 0.2);
}

.btn-primary:hover:not(:disabled) {
  background: linear-gradient(135deg, #c53030, #a53030);
  transform: translateY(-1px);
  box-shadow: 0 4px 8px rgba(219, 9, 8, 0.3);
}

.btn-primary:disabled {
  opacity: 0.6;
  cursor: not-allowed;
  transform: none;
}

.btn-full {
  width: 100%;
  padding: 16px 24px;
}

/* Animations */
.modal-enter-active,
.modal-leave-active {
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.modal-enter-from,
.modal-leave-to {
  opacity: 0;
  transform: scale(0.95);
}

.modal-enter-active .feedback-modal,
.modal-leave-active .feedback-modal {
  transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.modal-enter-from .feedback-modal,
.modal-leave-to .feedback-modal {
  transform: scale(0.9) translateY(20px);
}

/* Mobile Responsive */
@media (max-width: 768px) {
  .feedback-tab-container {
    right: 0;
    top: auto;
    bottom: 120px;
    transform: none;
  }
  
  .tab-content {
    border-radius: 12px 0 0 12px;
    width: 44px;
    padding: 12px 8px;
    min-height: 100px;
  }
  
  .tab-text {
    font-size: 12px;
  }
  
  .modal-overlay {
    padding: 16px;
    align-items: center;
  }
  
  .feedback-modal {
    width: 100%;
    max-width: 100%;
    border-radius: 20px;
    padding: 24px;
    max-height: 85vh;
  }
  
  .modal-header {
    flex-direction: column;
    text-align: center;
    gap: 12px;
  }
  
  .header-content h2 {
    font-size: 20px;
  }
  
  .form-row {
    grid-template-columns: 1fr;
    gap: 16px;
  }
  
  .form-input,
  .form-textarea,
  .form-select {
    font-size: 16px; /* Prevents zoom on iOS */
  }
}

@media (max-width: 480px) {
  .feedback-tab-container {
    bottom: 140px;
  }
  
  .tab-content {
    width: 40px;
    padding: 10px 6px;
    min-height: 90px;
  }
  
  .feedback-modal {
    padding: 20px;
  }
  
  .header-content h2 {
    font-size: 18px;
  }
}

/* Reduced motion */
@media (prefers-reduced-motion: reduce) {
  * {
    animation-duration: 0.01ms !important;
    animation-iteration-count: 1 !important;
    transition-duration: 0.01ms !important;
  }
}

/* High contrast mode */
@media (prefers-contrast: high) {
  .tab-content {
    border: 2px solid white;
  }
  
  .feedback-modal {
    border: 2px solid #000;
  }
  
  .form-input,
  .form-textarea,
  .form-select {
    border-width: 3px;
  }
}

/* Iconify icon fixes */
.iconify {
  display: inline-block;
  vertical-align: middle;
  flex-shrink: 0;
}

/* Loading animation */
.animate-spin {
  animation: spin 1s linear infinite;
}

@keyframes spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}
</style>

<script>
import { Icon } from '@iconify/vue';

export default {
  name: 'FeedbackTab',
  components: {
    Icon,
  },
  data() {
    return {
      isOpen: false,
      submitted: false,
      isSubmitting: false,
      form: {
        name: '',
        email: '',
        message: '',
        type: '',
        url: window.location.href,
        attachment: null,
        attachmentName: '',
        requestFollowup: false,
        browserInfo: this.getBrowserInfo(),
        deviceType: this.getDeviceType(),
        timestamp: new Date().toISOString(),
        pageTitle: document.title
      }
    };
  },
  mounted() {
    // Listen for keyboard events
    document.addEventListener('keydown', this.handleKeydown);
    
    // Track impression of the feedback tab
    this.trackEvent('feedback_tab_impression');
  },
  beforeUnmount() {
    document.removeEventListener('keydown', this.handleKeydown);
  },
  methods: {
    toggleFeedback() {
      this.isOpen = !this.isOpen;
      
      // Track event
      this.trackEvent(this.isOpen ? 'feedback_tab_open' : 'feedback_tab_close');
      
      // Handle focus management for accessibility
      if (this.isOpen) {
        this.$nextTick(() => {
          const firstInput = document.querySelector('.feedback-modal select, .feedback-modal input, .feedback-modal textarea');
          if (firstInput) firstInput.focus();
        });
      }
    },
    handleKeydown(e) {
      // Close modal on escape key
      if (e.key === 'Escape' && this.isOpen) {
        this.toggleFeedback();
      }
    },
    handleFileUpload(event) {
      const file = event.target.files[0];
      if (file) {
        this.form.attachment = file;
        this.form.attachmentName = file.name;
        this.trackEvent('feedback_file_attached');
      }
    },
    getBrowserInfo() {
      return {
        userAgent: navigator.userAgent,
        language: navigator.language,
        screenWidth: window.screen.width,
        screenHeight: window.screen.height,
        viewportWidth: window.innerWidth,
        viewportHeight: window.innerHeight
      };
    },
    getDeviceType() {
      const ua = navigator.userAgent;
      if (/(tablet|ipad|playbook|silk)|(android(?!.*mobi))/i.test(ua)) {
        return 'tablet';
      }
      if (/Mobile|Android|iP(hone|od)|IEMobile|BlackBerry|Kindle|Silk-Accelerated|(hpw|web)OS|Opera M(obi|ini)/.test(ua)) {
        return 'mobile';
      }
      return 'desktop';
    },
    trackEvent(eventName, properties = {}) {
      // Check if analytics are available
      if (window.gtag) {
        window.gtag('event', eventName, properties);
      } else if (window.dataLayer) {
        window.dataLayer.push({
          event: eventName,
          ...properties
        });
      }
      
      // Log event in console for development
      if (process.env.NODE_ENV !== 'production') {
        console.log('Feedback Tab Event:', eventName, properties);
      }
    },
    async submitFeedback() {
      if (!this.form.message || !this.form.type) {
        return;
      }
      
      this.isSubmitting = true;
      this.trackEvent('feedback_submit_attempt', { type: this.form.type });
      
      try {
        // Submit the feedback
        const formData = new FormData();
        formData.append('name', this.form.name);
        formData.append('email', this.form.email);
        formData.append('message', this.form.message);
        formData.append('type', this.form.type);
        formData.append('url', this.form.url);
        formData.append('requestFollowup', this.form.requestFollowup);
        formData.append('timestamp', this.form.timestamp);
        formData.append('pageTitle', this.form.pageTitle);
        formData.append('browserInfo', JSON.stringify(this.form.browserInfo));
        formData.append('deviceType', this.form.deviceType);
        
        // Add file attachment if present
        if (this.form.attachment) {
          formData.append('attachment', this.form.attachment);
        }

        // Get CSRF token
        const csrfToken = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content');
        
        // Enhanced logging for debugging
        console.log('=== FEEDBACK SUBMISSION DEBUG ===');
        console.log('CSRF Token:', csrfToken ? 'Found' : 'MISSING');
        console.log('Request URL:', '/api/submit-feedback');
        console.log('Request Method:', 'POST');
        console.log('FormData contents:');
        for (let [key, value] of formData.entries()) {
          if (value instanceof File) {
            console.log(`  ${key}: File(${value.name}, ${value.size} bytes, ${value.type})`);
          } else {
            console.log(`  ${key}: ${value}`);
          }
        }
        console.log('Request Headers:');
        console.log('  X-CSRF-TOKEN:', csrfToken);
        console.log('  Accept: application/json');
        console.log('================================');
        
        // Send request
        const response = await fetch('/api/submit-feedback', {
          method: 'POST',
          headers: {
            'X-CSRF-TOKEN': csrfToken,
            'Accept': 'application/json',
            // Remove Content-Type header to let browser set it for FormData
          },
          body: formData // Send FormData instead of JSON
        });
        
        // Enhanced response logging
        console.log('=== RESPONSE DEBUG ===');
        console.log('Response Status:', response.status);
        console.log('Response Status Text:', response.statusText);
        console.log('Response OK:', response.ok);
        console.log('Response Type:', response.type);
        console.log('Response URL:', response.url);
        console.log('Response Headers:');
        for (let [key, value] of response.headers.entries()) {
          console.log(`  ${key}: ${value}`);
        }
        console.log('=====================');
        
        if (response.ok) {
          const responseData = await response.json();
          console.log('SUCCESS Response Data:', responseData);
          this.submitted = true;
          this.trackEvent('feedback_submit_success', { type: this.form.type });
        } else {
          console.error('=== ERROR RESPONSE DEBUG ===');
          console.error('Status:', response.status, response.statusText);
          
          // Get response text first
          const responseText = await response.text();
          console.error('Raw Response Body:', responseText);
          
          // Check if it's HTML (error page) or JSON
          let errorData = null;
          if (responseText.trim().startsWith('<!DOCTYPE') || responseText.trim().startsWith('<html')) {
            console.error('ERROR: Server returned HTML error page instead of JSON');
            console.error('This indicates the request never reached Laravel/our controller');
            console.error('Possible causes:');
            console.error('- Apache/Nginx configuration issue');
            console.error('- Route not found');
            console.error('- Middleware blocking request');
            console.error('- Server overload');
          } else {
            try {
              errorData = JSON.parse(responseText);
              console.error('Parsed Error Data:', errorData);
            } catch (e) {
              console.error('Failed to parse response as JSON:', e);
            }
          }
          console.error('============================');

          this.trackEvent('feedback_submit_error', { 
            type: this.form.type,
            status: response.status,
            error: errorData?.message || 'Server returned HTML error page',
            isHtmlResponse: responseText.trim().startsWith('<!DOCTYPE') || responseText.trim().startsWith('<html')
          });
          
          alert(`Feedback submission failed (${response.status}). Check browser console for details.`);
        }
      } catch (error) {
        console.error('=== NETWORK/FETCH ERROR ===');
        console.error('Error Type:', error.constructor.name);
        console.error('Error Message:', error.message);
        console.error('Error Stack:', error.stack);
        console.error('===========================');
        
        this.trackEvent('feedback_submit_exception', { 
          type: this.form.type,
          error: error.message,
          errorType: error.constructor.name
        });
        alert('Network error occurred. Please check your connection and try again.');
      } finally {
        this.isSubmitting = false;
      }
    },
    resetForm() {
      this.form = {
        name: '',
        email: '',
        message: '',
        type: '',
        url: window.location.href,
        attachment: null,
        attachmentName: '',
        requestFollowup: false,
        browserInfo: this.getBrowserInfo(),
        deviceType: this.getDeviceType(),
        timestamp: new Date().toISOString(),
        pageTitle: document.title
      };
      this.submitted = false;
      
      // Reset file input
      if (this.$refs.fileInput) {
        this.$refs.fileInput.value = '';
      }
      
      this.trackEvent('feedback_form_reset');
    }
  }
};
</script> 